<script setup lang="ts">
import { ArrowRight, MapPin, Search as SearchLucide } from "lucide-vue-next";
import ProofTrustedProfilesIcon from "@/assets/icons/hero-proof/trusted-profiles.svg?component";
import ProofClearPricingIcon from "@/assets/icons/hero-proof/clear-pricing.svg?component";
import ProofAllLithuaniaIcon from "@/assets/icons/hero-proof/all-lithuania.svg?component";
import ProofFastSearchIcon from "@/assets/icons/hero-proof/fast-search.svg?component";
import ProofMoreConfidenceIcon from "@/assets/icons/hero-proof/more-confidence.svg?component";
import ProofVerifiedReviewsIcon from "@/assets/icons/hero-proof/verified-reviews.svg?component";
import ProofDirectContactIcon from "@/assets/icons/hero-proof/direct-contact.svg?component";
import ProofWorkExamplesIcon from "@/assets/icons/hero-proof/work-examples.svg?component";
import { getCategoryIconPath } from "~/utils/category-icons";
import {
  getCategorySearchPath,
  getServiceSearchPath,
  getWorkersSearchPath,
} from "~/utils/search-catalog";
import type {
  SearchCategoryOption,
  SearchRegionOption,
} from "~/utils/search-catalog";

const props = defineProps<{
  categories: SearchCategoryOption[];
  regions: SearchRegionOption[];
}>();

const localeRoute = useLocaleRoute();
const { t } = useI18n();

const cityQuery = ref("");
const cityComboboxRef = ref<{ openDropdown: () => void } | null>(null);
const mobileCityComboboxRef = ref<{ openDropdown: () => void } | null>(null);

const search = reactive({
  category: "",
  service: "",
  city: "",
});

function sortCatalog<T extends { sortOrder?: number | null; name: string }>(
  items: T[],
) {
  return [...items].sort(
    (a, b) =>
      Number(a.sortOrder == null) - Number(b.sortOrder == null) ||
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      a.name.localeCompare(b.name),
  );
}

function formatCategoryName(value: string) {
  const key = `category.${value}`;
  const translated = t(key);

  return translated === key ? value : translated;
}

function formatRegionName(value: string) {
  const key = `region.${value}`;
  const translated = t(key);

  return translated === key ? value : translated;
}

const cityOptions = computed(() => [
  { label: t("search.filters.location.all"), value: "" },
  ...sortCatalog(props.regions).map(region => ({
    label: formatRegionName(region.name),
    value: region.name,
  })),
]);

function categoryShortLabel(value: string) {
  const shortKey = `category_short.${value}`;
  const shortTranslated = t(shortKey);
  const translated =
    shortTranslated !== shortKey ? shortTranslated : formatCategoryName(value);

  if (value === "cleaning") {
    return t("hero.popular.cleaning_short");
  }

  if (value === "construction") {
    return t("hero.popular.construction_short");
  }

  if (value === "electricians") {
    return t("hero.popular.electrical_short");
  }

  return translated;
}

const heroPopularCategories = computed(() =>
  sortCatalog(props.categories)
    .slice(0, 5)
    .map(category => ({
      name: category.name,
      label: categoryShortLabel(category.name),
      icon: getCategoryIconPath(category.name),
    })),
);

const heroProofItems = [
  { key: "hero.proof.trusted_profiles", icon: ProofTrustedProfilesIcon },
  { key: "hero.proof.clear_pricing", icon: ProofClearPricingIcon },
  { key: "hero.proof.all_lithuania", icon: ProofAllLithuaniaIcon },
  { key: "hero.proof.fast_search", icon: ProofFastSearchIcon },
  { key: "hero.proof.more_confidence", icon: ProofMoreConfidenceIcon },
  { key: "hero.proof.verified_reviews", icon: ProofVerifiedReviewsIcon },
  { key: "hero.proof.direct_contact", icon: ProofDirectContactIcon },
  { key: "hero.proof.work_examples", icon: ProofWorkExamplesIcon },
];

const heroImages = [
  {
    src: "/images/home/hero-rotating/roofer-sunlit-roof.png",
    position: "67% 50%",
    mobilePosition: "78% 50%",
  },
  {
    src: "/images/home/hero-rotating/electrician-home-panel.png",
    position: "70% 35%",
    mobilePosition: "79% 38%",
  },
  {
    src: "/images/home/hero-rotating/plumber-under-sink.png",
    position: "70% 61%",
    mobilePosition: "78% 58%",
  },
  {
    src: "/images/home/hero-rotating/painter-bright-home.png",
    position: "69% 42%",
    mobilePosition: "77% 43%",
  },
  {
    src: "/images/home/hero-rotating/cleaning-bright-living-room.png",
    position: "69% 46%",
    mobilePosition: "78% 46%",
  },
];

const heroSocialProof = [
  { src: "/images/home/how-it-works/tomas.webp" },
  { src: "/images/home/how-it-works/ieva.webp" },
  { src: "/images/home/how-it-works/mantas.webp" },
];

const activeHeroImageIndex = ref(0);
const activeHeroImage = computed(
  () => heroImages[activeHeroImageIndex.value] ?? heroImages[0],
);

let heroRotationTimer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  heroImages.slice(1).forEach(({ src }) => {
    const image = new Image();
    image.src = src;
  });

  heroRotationTimer = setInterval(() => {
    activeHeroImageIndex.value =
      (activeHeroImageIndex.value + 1) % heroImages.length;
  }, 7000);
});

onBeforeUnmount(() => {
  if (heroRotationTimer) {
    clearInterval(heroRotationTimer);
  }
});

function openCityAfterCatalogSelection() {
  if (search.city) return;

  if (
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches
  ) {
    mobileCityComboboxRef.value?.openDropdown();
    return;
  }

  cityComboboxRef.value?.openDropdown();
}

function submitSearch() {
  if (!search.category) {
    return navigateTo(localeRoute(getWorkersSearchPath(search.city)));
  }

  if (search.service) {
    return navigateTo(
      localeRoute(
        getServiceSearchPath(search.category, search.service, search.city),
      ),
    );
  }

  return navigateTo(
    localeRoute(getCategorySearchPath(search.category, search.city)),
  );
}
</script>

<template>
  <section class="w-full bg-white">
    <div class="relative z-20 w-full overflow-visible bg-[#F8FAF9]">
      <div class="relative min-h-[650px] overflow-visible md:hidden">
        <div class="absolute inset-0 overflow-hidden">
          <Transition name="hero-mobile-image">
            <img
              :key="`mobile-${activeHeroImage.src}`"
              :src="activeHeroImage.src"
              alt=""
              class="absolute inset-0 h-full w-full object-cover"
              :style="{ objectPosition: activeHeroImage.mobilePosition }"
            />
          </Transition>

          <div
            class="absolute inset-0"
            style="
              background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 0.86) 0%,
                rgba(255, 255, 255, 0.72) 48%,
                rgba(255, 255, 255, 0.3) 76%,
                rgba(255, 255, 255, 0.08) 100%
              );
            "
          />
          <div
            class="absolute inset-0"
            style="
              background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.1) 0%,
                rgba(255, 255, 255, 0.04) 34%,
                rgba(255, 255, 255, 0.5) 72%,
                rgba(255, 255, 255, 0.78) 100%
              );
            "
          />
        </div>

        <div class="relative z-20 flex min-h-[650px] items-end px-4 pb-8 pt-24">
          <div class="w-full">
            <h1
              class="max-w-[350px] font-gabarito text-[34px] font-bold leading-[37px] tracking-[-0.026em] text-app-text"
            >
              {{ $t("hero.title.first") }}
              <span class="text-primary">{{ $t("hero.title.highlight") }}</span>
              {{ $t("hero.title.second") }}
              <br />
              {{ $t("hero.title.third") }}
            </h1>

            <p
              class="mt-3 max-w-[340px] text-[15px] leading-[22px] text-[#5F6966]"
            >
              {{ $t("hero.description") }}
            </p>

            <form
              class="relative z-[90] mt-5 flex w-full flex-col overflow-visible rounded-[18px] bg-white shadow-[0_14px_34px_rgba(19,49,41,.13)]"
              @submit.prevent="submitSearch"
            >
              <HomeHeroCatalogPicker
                v-model:category="search.category"
                v-model:service="search.service"
                :categories="categories"
                :placeholder="$t('hero.search.category_search_placeholder')"
                :input-class="
                  [
                    'group !h-[56px] items-center gap-3 !px-4 !py-0 transition duration-300',
                    'rounded-t-[17px] border-b border-[#E7ECEA] focus-within:bg-[#EEF6F3] focus-within:ring-0',
                    '[&>input]:h-full [&>input]:min-w-0 [&>input]:text-[16px] [&>input]:font-medium [&>input]:leading-[1.2] [&>input]::placeholder:text-[#8B9492]',
                    search.category || search.service
                      ? 'bg-[#E7F3EF] [&>input]:text-primary-800'
                      : 'bg-white hover:bg-[#FAFBFA] [&>input]:text-app-text',
                  ].join(' ')
                "
                @selection-complete="openCityAfterCatalogSelection"
              >
                <template #icon="{ active }">
                  <SearchLucide
                    class="size-5 shrink-0 stroke-[2] transition-colors duration-300"
                    :class="
                      active
                        ? 'text-primary'
                        : 'text-[#66706E] group-hover:text-primary group-focus-within:text-primary'
                    "
                  />
                </template>
              </HomeHeroCatalogPicker>

              <AppCombobox
                ref="mobileCityComboboxRef"
                v-model="search.city"
                v-model:search-text="cityQuery"
                :options="cityOptions"
                :placeholder="$t('hero.search.city_search_placeholder')"
                :not-found-text="$t('hero.search.city_not_found')"
                dropdown-min-height="min-h-[120px]"
                dropdown-max-height="max-h-[360px]"
                content-class="!left-0 !right-auto !z-[1200] !w-[calc(100vw-32px)] !min-w-0 max-w-[420px]"
                :input-class="
                  [
                    'group !h-[56px] items-center gap-3 !px-4 !py-0 transition duration-300',
                    'rounded-none border-b border-[#E7ECEA] focus-within:bg-[#EEF6F3] focus-within:ring-0',
                    '[&>input]:h-full [&>input]:min-w-0 [&>input]:text-[16px] [&>input]:font-medium [&>input]:leading-[1.2] [&>input]::placeholder:text-[#8B9492]',
                    search.city || cityQuery
                      ? 'bg-[#E7F3EF] [&>input]:text-primary-800'
                      : 'bg-white hover:bg-[#FAFBFA] [&>input]:text-app-text',
                  ].join(' ')
                "
                clear-button-class="!size-7 !border !border-[#CBE0DA] !bg-white text-primary shadow-sm hover:!bg-white"
                clear-icon-class="size-[16px]"
                show-chevron
                clear-invalid-on-close
              >
                <template #icon="{ active }">
                  <MapPin
                    class="size-5 shrink-0 stroke-[2] transition-colors duration-300"
                    :class="
                      active
                        ? 'text-primary'
                        : 'text-[#66706E] group-hover:text-primary group-focus-within:text-primary'
                    "
                  />
                </template>
              </AppCombobox>

              <button
                type="submit"
                class="flex min-h-[58px] items-center justify-center gap-2 rounded-b-[17px] bg-primary px-5 text-base font-semibold text-white transition duration-300 hover:bg-primary-400"
              >
                <SearchLucide class="size-5 shrink-0 stroke-[2.2]" />
                {{ $t("hero.search.action") }}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div
        class="relative z-20 hidden min-h-[520px] overflow-visible md:block lg:min-h-[540px]"
      >
        <div class="absolute inset-0 overflow-hidden">
          <Transition name="hero-desktop-image">
            <img
              :key="activeHeroImage.src"
              :src="activeHeroImage.src"
              alt=""
              class="absolute inset-0 h-full w-full object-cover"
              :style="{ objectPosition: activeHeroImage.position }"
            />
          </Transition>

          <div
            class="absolute inset-0"
            style="
              background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 0.78) 0%,
                rgba(255, 255, 255, 0.68) 24%,
                rgba(255, 255, 255, 0.42) 43%,
                rgba(255, 255, 255, 0.12) 58%,
                rgba(255, 255, 255, 0) 72%
              );
            "
          />
          <div
            class="absolute inset-0"
            style="
              background: linear-gradient(
                180deg,
                rgba(5, 15, 13, 0.03) 0%,
                rgba(5, 15, 13, 0) 62%,
                rgba(5, 15, 13, 0.06) 100%
              );
            "
          />
        </div>

        <div
          class="relative z-30 mx-auto flex min-h-[520px] w-full max-w-[1280px] items-center px-6 lg:min-h-[540px]"
        >
          <div class="relative w-full max-w-[700px]">
            <h1
              class="max-w-[610px] font-gabarito text-[43px] font-bold leading-[47px] tracking-[-0.028em] text-app-text lg:text-[48px] lg:leading-[51px]"
            >
              {{ $t("hero.title.first") }}
              <span class="text-primary">{{ $t("hero.title.highlight") }}</span>
              {{ $t("hero.title.second") }}
              <br />
              {{ $t("hero.title.third") }}
            </h1>

            <p
              class="mt-4 max-w-[520px] text-[15px] leading-6 text-[#5F6966] lg:text-base"
            >
              {{ $t("hero.description") }}
            </p>

            <form
              class="relative z-30 mt-6 flex w-full max-w-[640px] items-stretch overflow-visible rounded-[16px] bg-white shadow-[0_14px_36px_rgba(19,49,41,.12)]"
              @submit.prevent="submitSearch"
            >
              <div class="min-w-0 flex-1 border-r border-[#E7ECEA]">
                <HomeHeroCatalogPicker
                  v-model:category="search.category"
                  v-model:service="search.service"
                  :categories="categories"
                  :placeholder="$t('hero.search.category_search_placeholder')"
                  :input-class="
                    [
                      'group !h-[58px] items-center gap-3 !px-4 !py-0 transition duration-300',
                      'rounded-l-[13px] focus-within:bg-[#E7F3EF] focus-within:ring-0',
                      '[&>input]:h-full [&>input]:min-w-0 [&>input]:text-[16px] [&>input]:font-medium [&>input]:leading-[1.2] [&>input]::placeholder:text-[#8B9492]',
                      search.category || search.service
                        ? 'bg-[#EEF6F3] [&>input]:text-primary-800'
                        : 'bg-white hover:bg-[#FAFBFA] [&>input]:text-app-text',
                    ].join(' ')
                  "
                  @selection-complete="openCityAfterCatalogSelection"
                >
                  <template #icon="{ active }">
                    <SearchLucide
                      class="size-5 shrink-0 stroke-[2] transition-colors duration-300"
                      :class="
                        active
                          ? 'text-primary'
                          : 'text-[#596461] group-hover:text-primary group-focus-within:text-primary'
                      "
                    />
                  </template>
                </HomeHeroCatalogPicker>
              </div>

              <div class="min-w-0 w-[178px] lg:w-[184px]">
                <AppCombobox
                  ref="cityComboboxRef"
                  v-model="search.city"
                  v-model:search-text="cityQuery"
                  :options="cityOptions"
                  :placeholder="$t('hero.search.city_search_placeholder')"
                  :not-found-text="$t('hero.search.city_not_found')"
                  dropdown-min-height="min-h-[120px]"
                  dropdown-max-height="max-h-[360px]"
                  content-class="!left-0 !right-auto !w-[280px] !min-w-[280px] max-w-[calc(100vw-32px)]"
                  :input-class="
                    [
                      'group !h-[58px] items-center gap-3 !px-4 !py-0 transition duration-300',
                      'rounded-none focus-within:bg-[#E7F3EF] focus-within:ring-0',
                      '[&>input]:h-full [&>input]:min-w-0 [&>input]:text-[16px] [&>input]:font-medium [&>input]:leading-[1.2] [&>input]::placeholder:text-[#8B9492]',
                      search.city || cityQuery
                        ? 'bg-[#EEF6F3] [&>input]:text-primary-800'
                        : 'bg-white hover:bg-[#FAFBFA] [&>input]:text-app-text',
                    ].join(' ')
                  "
                  active-class="!bg-[#E7F3EF]"
                  clear-button-class="!size-6 !border !border-[#BDD8D0] !bg-white text-primary shadow-sm hover:!bg-white"
                  clear-icon-class="size-[15px]"
                  show-chevron
                  clear-invalid-on-close
                >
                  <template #icon="{ active }">
                    <MapPin
                      class="size-5 shrink-0 stroke-[2] transition-colors duration-300"
                      :class="
                        active
                          ? 'text-primary'
                          : 'text-[#596461] group-hover:text-primary group-focus-within:text-primary'
                      "
                    />
                  </template>
                </AppCombobox>
              </div>

              <button
                type="submit"
                class="flex min-h-[58px] min-w-[126px] items-center justify-center gap-2 rounded-r-[12px] bg-primary px-5 text-sm font-semibold text-white shadow-[0_6px_16px_rgba(19,122,104,.18)] transition duration-300 hover:bg-primary-400 lg:min-w-[132px]"
              >
                <SearchLucide class="size-[18px] shrink-0 stroke-[2.2]" />
                {{ $t("hero.search.action") }}
              </button>
            </form>

            <div class="mt-4 flex max-w-[640px] flex-nowrap items-center gap-2">
              <NuxtLink
                v-for="item in heroPopularCategories"
                :key="item.name"
                :to="localeRoute(getCategorySearchPath(item.name))"
                class="inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-white/85 bg-white/90 px-3.5 text-xs font-semibold text-[#26312E] shadow-[0_3px_10px_rgba(19,49,41,.05)] transition duration-300 hover:border-primary-200 hover:bg-white hover:text-primary-700"
              >
                {{ item.label }}
              </NuxtLink>
            </div>
          </div>

          <div
            class="pointer-events-none absolute bottom-7 right-6 hidden xl:flex"
          >
            <div
              class="flex items-center gap-3 rounded-[20px] px-4 py-3.5 shadow-[0_16px_36px_rgba(16,35,31,.15)]"
              style="background: rgba(255, 255, 255, 0.97)"
            >
              <div class="flex items-center">
                <img
                  v-for="(avatar, index) in heroSocialProof"
                  :key="avatar.src"
                  :src="avatar.src"
                  alt=""
                  class="size-10 rounded-full border-2 border-white object-cover shadow-sm"
                  :class="index > 0 ? '-ml-2.5' : ''"
                  aria-hidden="true"
                />
                <div
                  class="-ml-2.5 flex size-10 items-center justify-center rounded-full border-2 border-white bg-primary text-[15px] font-bold text-white shadow-sm"
                  aria-hidden="true"
                >
                  ✓
                </div>
              </div>

              <div class="min-w-0 pr-1">
                <div class="text-sm font-semibold leading-5 text-app-text">
                  {{ $t("hero.social_proof.title") }}
                </div>
                <div class="mt-0.5 text-xs leading-4 text-[#6A7371]">
                  {{ $t("hero.social_proof.description") }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="hidden bg-[#081416] text-white md:block">
      <div
        class="mx-auto flex min-h-[68px] w-full max-w-[1280px] flex-col justify-center gap-4 px-4 py-3 sm:px-6 md:flex-row md:items-center md:justify-between md:gap-8"
      >
        <p
          class="max-w-[920px] text-base font-semibold leading-6 text-white/96"
        >
          {{ $t("hero.trust_bar.prefix") }}
          <span class="text-primary-200">{{
            $t("hero.trust_bar.highlight")
          }}</span>
          {{ $t("hero.trust_bar.suffix") }}
        </p>

        <NuxtLink
          :to="localeRoute('/how-it-works')"
          class="group inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-white px-4 text-sm font-semibold text-app-text transition duration-300 hover:bg-primary-50 hover:text-primary-700"
        >
          {{ $t("hero.trust.action") }}
          <ArrowRight
            class="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5"
          />
        </NuxtLink>
      </div>
    </div>

    <div class="bg-primary-700 text-white">
      <div class="overflow-hidden">
        <div class="hero-proof-track flex w-max items-center py-2 md:py-2.5">
          <div
            v-for="copy in 2"
            :key="copy"
            class="flex w-max shrink-0 items-center justify-around"
          >
            <template
              v-for="item in heroProofItems"
              :key="`${copy}-${item.key}`"
            >
              <span
                class="inline-flex shrink-0 items-center gap-2 whitespace-nowrap px-4 text-xs font-medium tracking-[0.01em] text-white/90 sm:px-5"
              >
                <component
                  :is="item.icon"
                  class="size-[16px] shrink-0 text-primary-100/95"
                />
                {{ $t(item.key) }}
              </span>
              <span
                class="mx-1 size-[3px] shrink-0 rounded-full bg-white/28"
                aria-hidden="true"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
.hero-proof-track {
  animation: hero-proof-marquee 34s linear infinite;
  will-change: transform;
}

@keyframes hero-proof-marquee {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.hero-mobile-image-enter-active,
.hero-mobile-image-leave-active {
  transition:
    opacity 900ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 1250ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-mobile-image-enter-from {
  opacity: 0;
  transform: scale(1.035);
}

.hero-mobile-image-leave-to {
  opacity: 0;
  transform: scale(1.015);
}

.hero-desktop-image-enter-active,
.hero-desktop-image-leave-active {
  transition:
    opacity 1050ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 1450ms cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-desktop-image-enter-from {
  opacity: 0;
  transform: scale(1.025) translateX(10px);
}

.hero-desktop-image-leave-to {
  opacity: 0;
  transform: scale(1.012) translateX(-8px);
}

@media (prefers-reduced-motion: reduce) {
  .hero-proof-track {
    animation: none;
  }

  .hero-mobile-image-enter-active,
  .hero-mobile-image-leave-active,
  .hero-desktop-image-enter-active,
  .hero-desktop-image-leave-active {
    transition: opacity 220ms ease;
  }

  .hero-mobile-image-enter-from,
  .hero-mobile-image-leave-to,
  .hero-desktop-image-enter-from,
  .hero-desktop-image-leave-to {
    transform: none;
  }
}
</style>
